import React, { useEffect, useState, useCallback } from 'react'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Progress } from '@/components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import {
  Users,
  Search,
  Download,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Phone,
  Repeat,
  Crown,
  Wallet,
  Receipt,
  UtensilsCrossed,
  Package,
  Smartphone,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ShoppingBag,
  CalendarClock,
  CalendarPlus,
  SlidersHorizontal
} from 'lucide-react'
import whatsappIcon from '@/assets/WhatsApp.svg.webp'
import { useCurrency } from '@/hooks/useCurrency'
import { formatDate } from '@/lib/utils'
import { createDateRange, parseDate } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import { ApiService } from '@/services/api'
import type { Customer, CustomersSummary, PaginationInfo, LoyaltyTier } from '@/types'

const PAGE_SIZE = 24
const SKELETON_CARD_COUNT = 8

const EMPTY_SUMMARY: CustomersSummary = {
  totalCustomers: 0,
  totalVisits: 0,
  totalRevenue: 0,
  repeatCustomers: 0,
  vipCustomers: 0,
  averageSpendPerCustomer: 0
}

type SortOption = 'visits' | 'spend' | 'recent' | 'name' | 'loyalty'
type Segment = 'all' | 'new' | 'repeat' | 'vip'

// Points needed to reach each tier — mirrors getLoyaltyTier() on the backend.
const TIER_THRESHOLDS: Record<LoyaltyTier, number> = { Bronze: 0, Silver: 5, Gold: 10, Platinum: 20 }
const TIER_ORDER: LoyaltyTier[] = ['Bronze', 'Silver', 'Gold', 'Platinum']

const TIER_STYLES: Record<LoyaltyTier, { badge: string; ring: string; avatar: string }> = {
  Bronze: { badge: 'border-transparent bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300', ring: 'ring-orange-200 dark:ring-orange-900', avatar: 'bg-orange-500' },
  Silver: { badge: 'border-transparent bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200', ring: 'ring-slate-200 dark:ring-slate-700', avatar: 'bg-slate-500' },
  Gold: { badge: 'border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300', ring: 'ring-yellow-200 dark:ring-yellow-900', avatar: 'bg-yellow-500' },
  Platinum: { badge: 'border-transparent bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300', ring: 'ring-violet-200 dark:ring-violet-900', avatar: 'bg-violet-600' }
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function openWhatsApp(phoneNumber: string) {
  const digits = phoneNumber.replace(/\D/g, '')
  window.open(`https://wa.me/${digits}`, '_blank')
}

function nextTierProgress(tier: LoyaltyTier, points: number) {
  const idx = TIER_ORDER.indexOf(tier)
  if (idx === TIER_ORDER.length - 1) return { nextTier: null, pct: 100, pointsToGo: 0 }
  const nextTier = TIER_ORDER[idx + 1]
  const floor = TIER_THRESHOLDS[tier]
  const ceiling = TIER_THRESHOLDS[nextTier]
  const pct = Math.min(100, Math.round(((points - floor) / (ceiling - floor)) * 100))
  return { nextTier, pct, pointsToGo: Math.max(0, ceiling - points) }
}

export default function CustomersScreen() {
  const { formatCurrency } = useCurrency()
  const { currentRestaurant } = useRestaurant()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [summary, setSummary] = useState<CustomersSummary>(EMPTY_SUMMARY)
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 1 })
  const [isFullLoading, setIsFullLoading] = useState(true)
  const [isCardsLoading, setIsCardsLoading] = useState(false)
  const [errorScope, setErrorScope] = useState<'full' | 'cards'>('full')
  const [error, setError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [segment, setSegment] = useState<Segment>('all')
  const [sortBy, setSortBy] = useState<SortOption>('visits')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [allTime, setAllTime] = useState(true)
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const loadCustomers = useCallback(async (targetPage: number = 1, scope: 'full' | 'cards' = 'full') => {
    if (!currentRestaurant?._id) return
    setError(null)
    setErrorScope(scope)
    if (scope === 'full') setIsFullLoading(true)
    else setIsCardsLoading(true)

    try {
      const response = await ApiService.getCustomers(
        currentRestaurant._id,
        allTime ? undefined : dateRange,
        {
          page: targetPage,
          pageSize: PAGE_SIZE,
          search: searchTerm || undefined,
          segment,
          sortBy,
          sortOrder
        }
      )
      if (response.success) {
        setCustomers(response.data)
        setSummary(response.summary || EMPTY_SUMMARY)
        setPagination(response.pagination || { page: 1, pageSize: PAGE_SIZE, total: response.data.length, totalPages: 1 })
      } else {
        setError(response.message || 'Failed to load customers')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load customers')
    } finally {
      setIsFullLoading(false)
      setIsCardsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRestaurant?._id, allTime, dateRange, searchTerm, segment, sortBy, sortOrder])

  useEffect(() => {
    if (currentRestaurant?._id) loadCustomers(1, 'full')
  }, [currentRestaurant?._id, loadCustomers])

  const goToPage = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.totalPages || newPage === pagination.page) return
    loadCustomers(newPage, 'cards')
  }

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    const newDate = parseDate(value)
    setDateRange(prev => field === 'from' ? createDateRange(newDate, prev.to) : createDateRange(prev.from, newDate))
  }

  const exportToCSV = async () => {
    if (!currentRestaurant?._id) return
    setIsExporting(true)
    try {
      const response = await ApiService.getCustomers(currentRestaurant._id, allTime ? undefined : dateRange, {
        search: searchTerm || undefined,
        segment,
        sortBy,
        sortOrder
      })
      if (!response.success) {
        setError(response.message || 'Failed to export customers')
        return
      }

      const headers = ['Name', 'Phone', 'Visits', 'Loyalty Points', 'Tier', 'Total Paid', 'Average Bill', 'Dine-In Visits', 'Parcel Visits', 'Online Visits', 'First Visit', 'Last Visit']
      const rows = response.data.map(c => [
        c.name,
        c.phoneNumber,
        c.visitCount,
        c.loyaltyPoints,
        c.tier,
        c.totalPaid.toFixed(2),
        c.averageBillValue.toFixed(2),
        c.dineInVisits,
        c.parcelVisits,
        c.onlineVisits,
        formatDate(c.firstVisit),
        formatDate(c.lastVisit)
      ])

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => {
          const s = String(cell)
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
        }).join(','))
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `customers-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export customers')
    } finally {
      setIsExporting(false)
    }
  }

  const renderFilterFields = (searchId: string) => (
    <>
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-2 sm:col-span-2 lg:col-span-1">
          <Label htmlFor={searchId}>Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              id={searchId}
              placeholder="Name or phone number..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Segment</Label>
          <Select value={segment} onValueChange={(v) => setSegment(v as Segment)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              <SelectItem value="new">New (1 visit)</SelectItem>
              <SelectItem value="repeat">Repeat (2+ visits)</SelectItem>
              <SelectItem value="vip">VIP (Gold & Platinum)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Sort By</Label>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="visits">Most Visits</SelectItem>
              <SelectItem value="spend">Highest Spend</SelectItem>
              <SelectItem value="loyalty">Loyalty Points</SelectItem>
              <SelectItem value="recent">Most Recent Visit</SelectItem>
              <SelectItem value="name">Name A-Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Order</Label>
          <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as 'asc' | 'desc')}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Highest First</SelectItem>
              <SelectItem value="asc">Lowest First</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2 border-t">
        <div className="flex items-center space-x-2">
          <Switch id={`${searchId}-all-time`} checked={allTime} onCheckedChange={setAllTime} />
          <Label htmlFor={`${searchId}-all-time`} className="cursor-pointer">All-time totals</Label>
        </div>
        {!allTime && (
          <DateRangeModal
            dateRange={dateRange}
            onDateChange={handleDateChange}
            onApply={() => loadCustomers(1, 'full')}
            title="Customer Visit Period"
            description="Only visits within this range count toward the stats below"
            triggerText="Select Period"
          />
        )}
      </div>
    </>
  )

  if (isFullLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2 text-muted-foreground">Loading customers...</span>
        </div>
      </div>
    )
  }

  if (error && errorScope === 'full') {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Unable to load customers</AlertTitle>
          <AlertDescription>
            <p className="mb-3">{error}</p>
            <Button variant="outline" size="sm" className="py-[10px]" onClick={() => loadCustomers(1, 'full')}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">Customers</h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            Guests, visit history & loyalty
          </p>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <Button onClick={exportToCSV} variant="outline" size="icon" className="sm:w-auto sm:h-auto sm:px-4 py-[10px]" disabled={isExporting}>
            {isExporting ? <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" /> : <Download className="h-4 w-4 sm:mr-2" />}
            <span className="hidden sm:inline">Export Excel</span>
          </Button>
          <Button onClick={() => loadCustomers(pagination.page, 'full')} variant="outline" size="icon" className="sm:w-auto sm:h-auto sm:px-4 py-[10px]">
            <RefreshCw className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {error && errorScope === 'cards' && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Filters (desktop / tablet) */}
      <Card className="hidden md:block">
        <CardHeader>
          <CardTitle className="text-base flex items-center space-x-2">
            <Search className="h-4 w-4" />
            <span>Find Customers</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderFilterFields('customer-search')}
        </CardContent>
      </Card>

      {/* Filters (mobile floating button + bottom sheet) */}
      <div className="fixed bottom-40 right-4 z-50 md:hidden">
        <Button
          size="lg"
          onClick={() => setIsFilterSheetOpen(true)}
          className="h-14 w-14 rounded-full shadow-lg bg-yellow-500 text-black hover:bg-yellow-600 flex items-center justify-center border-2 border-white"
          aria-label="Find customers"
        >
          <SlidersHorizontal className="h-6 w-6" style={{ color: 'black', stroke: 'black' }} />
        </Button>
      </div>
      <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
        <SheetContent side="bottom" className="md:hidden">
          <SheetHeader>
            <SheetTitle className="flex items-center space-x-2">
              <Search className="h-4 w-4" />
              <span>Find Customers</span>
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-5">
            {renderFilterFields('customer-search-mobile')}
          </div>
        </SheetContent>
      </Sheet>

      {/* Summary Stats */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Customers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{summary.totalCustomers}</div>
            <p className="text-xs text-muted-foreground">Unique guests</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Visits</CardTitle>
            <Repeat className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{summary.totalVisits}</div>
            <p className="text-xs text-muted-foreground">All bills, all guests</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Repeat Guests</CardTitle>
            <Sparkles className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">
              {summary.totalCustomers > 0 ? Math.round((summary.repeatCustomers / summary.totalCustomers) * 100) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">{summary.repeatCustomers} came back</p>
          </CardContent>
        </Card>
        <Card className="col-span-2 lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Spend</CardTitle>
            <Crown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(summary.averageSpendPerCustomer)}</div>
            <p className="text-xs text-muted-foreground">{summary.vipCustomers} VIP guests</p>
          </CardContent>
        </Card>
      </div>

      {/* Customer Cards */}
      {isCardsLoading ? (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
            <Card key={`skeleton-${i}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center space-x-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : customers.length === 0 ? (
        <Card>
          <CardContent className="py-12 flex flex-col items-center space-y-2">
            <Users className="h-10 w-10 text-muted-foreground" />
            <span className="text-muted-foreground">No customers found</span>
            <span className="text-sm text-muted-foreground">
              Try adjusting your search, segment or date range
            </span>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {customers.map((customer) => {
            const style = TIER_STYLES[customer.tier]
            return (
              <div
                key={customer.phoneNumber}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedCustomer(customer)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedCustomer(customer) }}
                className="text-left cursor-pointer"
              >
                <Card className={`h-full transition-shadow hover:shadow-md ring-1 ${style.ring}`}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white text-sm font-semibold ${style.avatar}`}>
                          {getInitials(customer.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{customer.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {customer.phoneNumber}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          title="Message on WhatsApp"
                          onClick={(e) => { e.stopPropagation(); openWhatsApp(customer.phoneNumber) }}
                          className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-green-100 dark:hover:bg-green-950"
                        >
                          <img src={whatsappIcon} alt="WhatsApp" className="h-6 w-6" />
                        </button>
                        <Badge className={`shrink-0 ${style.badge}`}>{customer.tier}</Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-muted-foreground text-xs">Visits</div>
                        <div className="font-semibold">{customer.visitCount}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs">Loyalty Points</div>
                        <div className="font-semibold">{customer.loyaltyPoints} pts</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs">Total Paid</div>
                        <div className="font-semibold">{formatCurrency(customer.totalPaid)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-xs">Avg Bill</div>
                        <div className="font-semibold">{formatCurrency(customer.averageBillValue)}</div>
                      </div>
                    </div>

                    {(customer.dineInVisits > 0 || customer.parcelVisits > 0 || customer.onlineVisits > 0) && (
                      <div className="flex flex-wrap gap-1.5">
                        {customer.dineInVisits > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            <UtensilsCrossed className="h-3 w-3" />{customer.dineInVisits} dine-in
                          </span>
                        )}
                        {customer.parcelVisits > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            <Package className="h-3 w-3" />{customer.parcelVisits} parcel
                          </span>
                        )}
                        {customer.onlineVisits > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            <Smartphone className="h-3 w-3" />{customer.onlineVisits} online
                          </span>
                        )}
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground pt-1 border-t">
                      Last visit: {formatDate(customer.lastVisit)}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages} • {pagination.total} customers
          </span>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" className="py-[10px]" onClick={() => goToPage(pagination.page - 1)} disabled={pagination.page <= 1 || isCardsLoading}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Prev
            </Button>
            <Button variant="outline" size="sm" className="py-[10px]" onClick={() => goToPage(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages || isCardsLoading}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Customer Detail Modal */}
      <Dialog open={!!selectedCustomer} onOpenChange={(open) => !open && setSelectedCustomer(null)}>
        <DialogContent className="max-w-md">
          {selectedCustomer && (() => {
            const style = TIER_STYLES[selectedCustomer.tier]
            const progress = nextTierProgress(selectedCustomer.tier, selectedCustomer.loyaltyPoints)
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center space-x-3">
                    <div className={`h-12 w-12 shrink-0 rounded-full flex items-center justify-center text-white text-base font-semibold ${style.avatar}`}>
                      {getInitials(selectedCustomer.name)}
                    </div>
                    <div className="min-w-0">
                      <DialogTitle className="truncate">{selectedCustomer.name}</DialogTitle>
                      <DialogDescription className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {selectedCustomer.phoneNumber}
                      </DialogDescription>
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge className={style.badge}>{selectedCustomer.tier} Tier</Badge>
                    <span className="text-sm font-medium">{selectedCustomer.loyaltyPoints} loyalty pts</span>
                  </div>

                  {progress.nextTier && (
                    <div className="space-y-1.5">
                      <Progress value={progress.pct} />
                      <p className="text-xs text-muted-foreground">
                        {progress.pointsToGo} more visit{progress.pointsToGo === 1 ? '' : 's'} to reach {progress.nextTier}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border p-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Repeat className="h-3.5 w-3.5" />Visits</div>
                      <div className="text-lg font-bold">{selectedCustomer.visitCount}</div>
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Wallet className="h-3.5 w-3.5" />Total Paid</div>
                      <div className="text-lg font-bold">{formatCurrency(selectedCustomer.totalPaid)}</div>
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Receipt className="h-3.5 w-3.5" />Avg Bill</div>
                      <div className="text-lg font-bold">{formatCurrency(selectedCustomer.averageBillValue)}</div>
                    </div>
                    <div className="rounded-lg border p-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><ShoppingBag className="h-3.5 w-3.5" />Items Ordered</div>
                      <div className="text-lg font-bold">{selectedCustomer.itemsOrdered}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CalendarPlus className="h-4 w-4" />
                      <span>First visit: <span className="text-foreground font-medium">{formatDate(selectedCustomer.firstVisit)}</span></span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <CalendarClock className="h-4 w-4" />
                      <span>Last visit: <span className="text-foreground font-medium">{formatDate(selectedCustomer.lastVisit)}</span></span>
                    </div>
                  </div>

                  {(selectedCustomer.dineInVisits > 0 || selectedCustomer.parcelVisits > 0 || selectedCustomer.onlineVisits > 0) && (
                    <div className="flex flex-wrap gap-2">
                      {selectedCustomer.dineInVisits > 0 && (
                        <Badge variant="outline" className="gap-1"><UtensilsCrossed className="h-3 w-3" />{selectedCustomer.dineInVisits} Dine-In</Badge>
                      )}
                      {selectedCustomer.parcelVisits > 0 && (
                        <Badge variant="outline" className="gap-1"><Package className="h-3 w-3" />{selectedCustomer.parcelVisits} Parcel</Badge>
                      )}
                      {selectedCustomer.onlineVisits > 0 && (
                        <Badge variant="outline" className="gap-1"><Smartphone className="h-3 w-3" />{selectedCustomer.onlineVisits} Online</Badge>
                      )}
                    </div>
                  )}
                </div>
              </>
            )
          })()}
        </DialogContent>
      </Dialog>
    </div>
  )
}
